<template>
  <!-- Kit MiniPlayer (the phone boards' floating player): a 62 px card 8
       from each side and 30 above the bottom, art 44, title and "Creator ·
       Session · D · 120", play/pause, then share or the price pill. While
       the sound is still downloading, a 2 px violet line inset 16 along the
       foot fills with the loaded fraction (SIGNED_IN_PLAN 3.5, the app's
       audition_player.dart); it goes once the sound has loaded. The strip
       under it stays as the seek target. -->
  <div v-if="item" class="mini-player" :class="{ 'has-price': !!item.pricePill }" :style="{ '--sound': item.color || 'var(--cl-accent)' }">
    <ArtTile :seed="item.art?.seed || item.id" :owner="item.art?.owner || item.color" :picture="item.art?.picture || null" pooled :size="44" :radius="8" />
    <div class="mini-titles" @click="onTitleClick">
      <div class="mini-title">{{ item.title }}</div>
      <div class="mini-sub">{{ sub }}</div>
    </div>
    <button type="button" class="mini-play" :aria-label="state.playing ? 'Pause' : 'Play'" @click="toggle">
      <v-progress-circular v-if="state.buffering" indeterminate size="20" width="2" color="var(--cl-label)" />
      <svg v-else-if="state.playing" width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><rect x="6" y="5" width="4" height="14" rx="1" /><rect x="14" y="5" width="4" height="14" rx="1" /></svg>
      <svg v-else width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5.5v13l11-6.5z" /></svg>
    </button>
    <button v-if="item.pricePill" type="button" class="mini-price" @click="item.pricePill.onClick && item.pricePill.onClick()">
      {{ item.pricePill.label }}
    </button>
    <button v-else-if="item.share" type="button" class="mini-share" aria-label="Share" @click="item.share()">
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 3v12M8 7l4-4 4 4" /><path d="M6 11v8a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1v-8" /></svg>
    </button>
    <div class="bar-progress" :class="{ seekable: !!state.source }" @click="onStripClick">
      <div v-if="loading" class="bar-loading" :style="{ width: loadedPct + '%' }" />
    </div>
  </div>
</template>

<script>
import ArtTile from "@/components/kit/ArtTile.vue";
import nowPlaying from "@/audio/nowPlaying.js";

export default {
  name: "MiniPlayer",
  components: { ArtTile },
  data: () => ({ state: nowPlaying.state }),
  computed: {
    item() {
      return this.state.item;
    },
    // Loading: buffering, or an audition not yet fully downloaded (the
    // session engine reports no loaded fraction).
    loading() {
      const s = this.state;
      if (!s.source) return false;
      return s.buffering || (s.source === "audition" && s.loaded < 100);
    },
    loadedPct() {
      return Math.max(0, Math.min(100, Number(this.state.loaded) || 0));
    },
    sub() {
      const i = this.item;
      if (!i) return "";
      return [i.subtitle, i.keyBpm || i.meta].filter(Boolean).join(" · ");
    },
  },
  methods: {
    toggle() {
      nowPlaying.toggle();
    },
    onStripClick(e) {
      if (!this.state.source) return;
      const box = e.currentTarget.getBoundingClientRect();
      if (box.width) nowPlaying.seek((e.clientX - box.left) / box.width);
    },
    onTitleClick() {
      if (this.item?.sessionName) {
        this.$router.push(`/session/${encodeURIComponent(this.item.sessionName)}`).catch(() => {});
      }
    },
  },
};
</script>

<style scoped>
.mini-player {
  position: fixed;
  left: 8px;
  right: 8px;
  bottom: calc(30px + env(safe-area-inset-bottom, 0px));
  z-index: 40;
  height: 62px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 8px 0 9px;
  border-radius: 16px;
  background: var(--cl-mini-player);
  border: 1px solid var(--border-subtle);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
  overflow: hidden;
  box-sizing: border-box;
}
.mini-titles {
  flex: 1;
  min-width: 0;
}
.mini-title {
  font: 600 15px/20px var(--cl-font);
  color: var(--cl-label);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.mini-sub {
  font: 400 12px/16px var(--cl-font);
  color: var(--cl-label-55);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.mini-play,
.mini-share {
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  border: none;
  background: transparent;
  color: var(--cl-label);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.mini-share {
  color: var(--cl-accent-text);
}
/* ProfileLoops' price pill: the filled violet button, 34 tall, with the
   card padded 10 on its right (Sounds' share keeps 8). */
.mini-player.has-price {
  padding-right: 10px;
}
.mini-price {
  flex-shrink: 0;
  height: 34px;
  padding: 0 14px;
  border: none;
  border-radius: 17px;
  background: var(--cl-accent);
  color: var(--cl-on-fill);
  font: 600 15px/1 var(--cl-font);
  white-space: nowrap;
  cursor: pointer;
}
/* 16 from the card's outer edges: 15 inside its 1 px border. */
.bar-progress {
  position: absolute;
  left: 15px;
  right: 15px;
  bottom: 0;
  height: 2px;
}
.bar-progress.seekable {
  cursor: pointer;
}
.bar-loading {
  height: 100%;
  border-radius: 1px;
  background: var(--cl-accent);
}
</style>
